import { z } from "zod";
import { ReactNode } from "react";
import { IAccess } from "./access";
import { PetInteractionLevel } from "../constants/pet.constants";

/**
 * Pet behavior schema
 */
export const petBehaviorSchema = z.object({
  value: z.string(),
  label: z.string(),
  isCustom: z.boolean()
});

export type PetBehavior = z.infer<typeof petBehaviorSchema>;

/**
 * Pet care note schema
 */
export const petCareNoteSchema = z.object({
  value: z.string(),
  label: z.string(),
  category: z.enum(["WALKING", "GROOMING", "FOOD"]),
  isCustom: z.boolean()
});

export type PetCareNote = z.infer<typeof petCareNoteSchema>;

/**
 * Pet date of birth schema
 */
export const petDobSchema = z
  .object({
    /**
     * Day of birth
     */
    day: z
      .number()
      .min(1)
      .max(31)
      .int()
      .refine(val => Number.isInteger(val), {
        message: "Day must be an integer",
      }),

    /**
     * Month of birth
     */
    month: z
      .number()
      .min(1)
      .max(12)
      .int()
      .refine(val => Number.isInteger(val), {
        message: "Month must be an integer",
      }),

    /**
     * Year of birth
     */
    year: z
      .number()
      .min(1900)
      .max(new Date().getFullYear())
      .int()
      .refine(val => Number.isInteger(val), {
        message: "Year must be an integer",
      }),
  })
  .refine(
    data => {
      // Validate the date is real (handles leap years, month lengths, etc)
      const date = new Date(data.year, data.month - 1, data.day);
      return (
        date.getFullYear() === data.year &&
        date.getMonth() === data.month - 1 &&
        date.getDate() === data.day
      );
    },
    {
      message: "Invalid date - this date doesn't exist in the calendar",
      path: ["day"], // The error will be assigned to the day field
    },
  );

export type PetDOB = z.infer<typeof petDobSchema>;

/**
 * Breed schema
 */
export const breedSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  code: z.string(),
  petType: z.string().optional(),
  isCommon: z.boolean().optional()
});

export type BreedData = z.infer<typeof breedSchema>;

/**
 * Pet breed response schema from API
 */
export const petBreedResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  petType: z.enum(["Dog", "Cat", "Other"]),
  isCommon: z.boolean().optional(),
  description: z.string().optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
});

export type IPetBreedResponse = z.infer<typeof petBreedResponseSchema>;

/**
 * Pet breeds list response schema
 */
export const petBreedsListResponseSchema = z.object({
  items: z.array(petBreedResponseSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    totalCount: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
  }),
});

export type IPetBreedsListResponse = z.infer<typeof petBreedsListResponseSchema>;

export const petNotesResponseSchema = z.object({
  comment: z.string().nullish(),
  tags: z.array(z.string()).nullish(),
  comfortWithPeople: z.nativeEnum(PetInteractionLevel).nullish(),
  reactsAroundOtherDogs: z.nativeEnum(PetInteractionLevel).nullish(),
});

const petNotesByCategorySchema = z.object({
  WALKING: petNotesResponseSchema.optional(),
  GROOMING: petNotesResponseSchema.optional(),
  FOOD: petNotesResponseSchema.optional(),
});

/**
 * Pet schema with complete validation
 */
export const petSchema = z.object({
  id: z.string().optional(),
  _id: z.string().optional(),
  name: z.string().min(1).max(100),
  species: z.enum(["dog", "cat", "other"]).optional(), // Optional for backward compatibility
  breed: breedSchema.optional().nullable(), // Can be string or breed object
  breedCode: z.string().optional(),
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).optional(),
  dob: petDobSchema.optional(),
  dateOfBirth: z.union([z.date(), z.number()]).optional(), // Can be Date object or timestamp
  weight: z.number().positive().optional(),
  profilePicUrl: z.string().url("Must be a valid URL").nullable().optional(),
  avatar: z.string().optional(), // For backward compatibility
  description: z.string().optional(),
  bio: z.string().max(500).optional(),
  behaviour: z.array(petBehaviorSchema).optional(),
  careNotes: z.array(petCareNoteSchema).optional(),
  characteristics: z.array(z.string()).optional(),
  owner: z.string().optional(),
  createdAt: z.union([z.date(), z.number()]).optional(), // Can be Date object or timestamp
  updatedAt: z.union([z.date(), z.number()]).optional(), // Can be Date object or timestamp
  age: z.number().optional(), // Calculated from dateOfBirth
  isDeleted: z.boolean().optional(),
  petNotes: petNotesByCategorySchema.optional().nullable(),
});

export type PetData = z.infer<typeof petSchema>;

/**
 * Pet profile schema for public info responses (simplified version)
 * Used in user basic public info responses
 */
export const petProfilePublicInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  breedName: z.string(),
  profilePicUrl: z.string().url().nullable(),
});

export type IPetProfilePublicInfo = z.infer<typeof petProfilePublicInfoSchema>;

export interface PetModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (pet: PetData) => void;
  pet?: PetData | null; // Existing pet data for editing
  access?: IAccess;
}

/**
 * PetsList props interface
 */
export interface PetsListProps {
  pets: PetData[];
  handleAddPet: () => void;
  handleEditPet: (pet: PetData) => void;
  handleDeletePet: (petId: string) => void;
  access?: IAccess;
}

/**
 * PetItem props interface
 */
export interface PetItemProps {
  pet: PetData;
  handleEditPet: (pet: PetData) => void;
  handleDeletePet: (petId: string) => void;
  access?: IAccess;
}

// Type for pet disabled fields
export interface PetDisabledFields {
  name: boolean;
  breedCode: boolean;
  gender: boolean;
  dob: boolean;
}

export interface PetError {
  name?: string;
  breedCode?: string;
  dob?: string;
}

export interface PetFormFieldProps {
  id: string;
  label: string;
  required?: boolean;
  children: ReactNode;
  error?: string;
  fieldName: keyof PetDisabledFields;
  disabled: boolean;
  onClick: (fieldName: string) => void;
}

export interface PetFormFieldsProps {
  petData: PetData;
  disabledFields: PetDisabledFields;
  handleFieldClick: (fieldName: string) => void;
  handleInputChange: (field: keyof PetData, value: string | number | Date | undefined) => void;
  errors: PetError;
}
